import { useState } from 'react'

import { ConfirmDialog } from 'src/shared/components/ui/popups/confirm-dialog'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { useDeleteNote } from '../../api/use-delete-note'
import type { BusinessNote } from '../../schemas/note-schema'
import { CalendarDate } from '../calendar-date'
import { NoteDropdown } from '../note-dropdown'
import { NoteModal } from '../note-modal'

interface NoteListItemProps {
  note: BusinessNote
}

export const NoteListItem = ({ note }: NoteListItemProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const deleteNote = useDeleteNote()
  const isMobile = useIsMobile()
  const handleEdit = () => {
    setIsOpen(true)
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    const params = {
      id: note.id,
      referralShareId: note.referralShareId || undefined,
      leadInquiryId: note.leadInquiryId || undefined,
    }

    deleteNote.mutate(params)
    setShowDeleteConfirm(false)
  }

  return (
    <>
      <div className={cn('flex items-start gap-4 p-4 transition-colors hover:bg-accent/10', isMobile && 'px-1 py-4')}>
        {/* Calendar Date (Left side) */}
        <CalendarDate date={note.updatedAt} />

        {/* Content (Right side) */}
        <div className="flex-1 overflow-hidden">
          {/* Title and Actions Row */}
          <div className="mb-2 flex items-start justify-between gap-5">
            <h3 className={cn('text-lg font-bold', isMobile && 'text-base')}>{note.title}</h3>
            <NoteDropdown onEdit={handleEdit} onDelete={handleDeleteClick} />
          </div>

          {/* Note Content */}
          <p className={cn('whitespace-pre-wrap text-muted', isMobile && 'text-sm')}>{note.content}</p>
        </div>
      </div>

      <NoteModal isOpen={isOpen} onClose={() => setIsOpen(false)} note={note} />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
